import { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { palette } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types';
import { formatTime12, ENTRY_TYPE_LABELS, getEntryColor } from '../../components/schedule/scheduleHelpers';

interface Props {
  entries: ScheduleEntry[];
  onEntryClick: (entry: ScheduleEntry) => void;
}

function DraggableCard({ entry, onEntryClick }: { entry: ScheduleEntry; onEntryClick: (e: ScheduleEntry) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: entry.id, data: { entry } });
  const color = getEntryColor(entry);
  const label = ENTRY_TYPE_LABELS[entry.type as keyof typeof ENTRY_TYPE_LABELS] ?? entry.type;

  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      onClick={() => { if (!isDragging) onEntryClick(entry); }}
      style={{
        minWidth: 180, padding: '8px 10px', borderRadius: 6, cursor: isDragging ? 'grabbing' : 'grab',
        background: color + '10', border: `1px solid ${color}30`, borderLeft: `3px solid ${color}`,
        flexShrink: 0, transition: 'border-color 0.12s, opacity 0.15s',
        opacity: isDragging ? 0.3 : 1, touchAction: 'none',
      }}
      onMouseEnter={e => { if (!isDragging) e.currentTarget.style.borderColor = color; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = color + '30'; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
        <span style={{ fontSize: 9, fontWeight: 600, color, textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: palette.g800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.title}</div>
      <div style={{ fontSize: 10, color: palette.g500 }}>
        {entry.all_day ? 'All day' : `${formatTime12(entry.start_at)} – ${formatTime12(entry.end_at)}`}
      </div>
      <div style={{ fontSize: 10, color: palette.red, fontWeight: 600, marginTop: 2 }}>Drag to assign</div>
    </div>
  );
}

export function UnassignedBar({ entries, onEntryClick }: Props) {
  const [expanded, setExpanded] = useState(true);

  if (entries.length === 0) return null;

  return (
    <div style={{ marginBottom: 12, background: palette.white, borderRadius: 10, border: `1px solid ${palette.g150}`, overflow: 'hidden' }}>
      <button onClick={() => setExpanded(!expanded)} style={{
        display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '10px 14px',
        border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
      }}>
        {expanded ? <ChevronDown size={14} color={palette.g500} /> : <ChevronRight size={14} color={palette.g500} />}
        <span style={{ fontSize: 13, fontWeight: 600, color: palette.g700 }}>Unassigned</span>
        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: entries.length > 3 ? palette.redWash : palette.amberWash, color: entries.length > 3 ? palette.red : palette.amber }}>{entries.length}</span>
      </button>

      {expanded && (
        <div style={{ display: 'flex', gap: 8, padding: '0 14px 12px', overflowX: 'auto' }}>
          {entries.map(entry => (
            <DraggableCard key={entry.id} entry={entry} onEntryClick={onEntryClick} />
          ))}
        </div>
      )}
    </div>
  );
}
